import type { ComputedRef } from 'vue';
import { watch } from 'vue';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { isDefined } from '~/utils/guards/isDefined';
import type { StoreConfig } from '~~/lib/Shop/generated/schema';
import { isStoreConfigRequiredRoute } from '~/utils/shop/isStoreConfigRequiredRoute';
import { shouldLoadMagentoStoreConfig } from '~/utils/shop/shouldLoadMagentoStoreConfig';

export type RelevantStoreConfig = Pick<
  StoreConfig,
  | 'store_code'
  | 'grid_per_page'
  | 'checkout_coupon_code_active'
  | 'sales_minimum_order_active'
  | 'sales_minimum_order_amount'
  | 'sales_minimum_order_description'
  | 'sales_minimum_order_tax_including'
  | 'sales_minimum_order_include_discount_amount'
  | 'maxqty_enabled'
  | 'maxqty_attribute_sets'
  | 'maxqty_qty'
  | 'crossselling_sku_tooltip_enabled'
  | 'tax_exemption_enabled'
  | 'tax_exemption_selected_region'
  | 'tax_exemption_file_size'
  | 'tax_exemption_file_extensions'
  | 'tax_exemption_field_title'
  | 'tax_exemption_field_info'
  | 'tax_exemption_check_sap_bp'
  | 'adyen_store_locale'
  | 'adyen_client_key_live'
  | 'adyen_client_key_test'
  | 'adyen_demo_mode'
  | 'adyen_has_holder_name'
  | 'adyen_holder_name_required'
  | 'adyen_merchant_account'
  | 'adyen_billie_checkout_description'
  | 'adyen_payment_method_title_overrides'
  | 'region_required_countries'
  | 'vat_validation_enabled'
  | 'vat_validation_frontend_enabled'
  | 'vat_validation_config'
  | 'quote_form_specificcountry'
  | 'enduser_statement_countries'
  | 'quote_form_language'
  | 'quote_form_reason_for_inquiry'
  | 'quote_form_agreement'
  | 'freight_forwarder_options'
>;

export default defineNuxtPlugin(async () => {
  const { $locale, $globalPageSettings } = useNuxtApp();

  function createProductDetailLink(product: DecoratedProduct) {
    const locale = useLocale();

    return buildPdpUrl(locale.value, product.urlKey ?? '');
  }

  const configuration = usePdpConfiguration();

  const shopStore: ComputedRef<string> = computed<string>(
    () =>
      configuration.value?.localeStoreMap?.[$locale.value] ??
      configuration.value?.localeStoreMap?.default ??
      'emea_en'
  );

  const usesDemoShop = useState<boolean>('usesDemoShop');
  const storeConfig = useState<RelevantStoreConfig>('storeConfig');

  if (!isDefined(usesDemoShop.value)) {
    const config = useRuntimeConfig().magento;

    const magentoUrl = (() => {
      if ($locale.value === 'zh-cn') {
        return config.configurationByLocale?.['zh-cn']?.graphql.url;
      }
      return config.defaultConfiguration.graphql.url;
    })();

    usesDemoShop.value = !magentoUrl?.startsWith('https://shop.baslerweb');
  }

  /**
   * Magento store config must follow the active Contentful `localeStoreMap`
   * (e.g. amer_en for en-us). Previously it was loaded once into `useState`:
   * wrong default `emea_en` while global page settings were still loading, or
   * stale EMEA config after switching to Americas/APAC on the client.
   */
  let storeConfigLoadGeneration = 0;

  async function refreshMagentoStoreConfig() {
    if ($locale.value === 'zh-cn') {
      storeConfigLoadGeneration++;
      storeConfig.value = {};
      return;
    }

    const generation = ++storeConfigLoadGeneration;
    const webappLocale = convertContentfulToWebappLocaleCode($locale.value);
    const magentoStore = shopStore.value;

    try {
      const storeConfigRef = await useStoreConfig(webappLocale, magentoStore);
      if (generation !== storeConfigLoadGeneration) {
        return;
      }
      storeConfig.value = storeConfigRef.value ?? {};
    } catch (error) {
      if (generation !== storeConfigLoadGeneration) {
        return;
      }
      if (import.meta.server) {
        const { container } = await import('~~/lib/Container');
        container
          .get('logger')
          .error('plugin/shop: Could not load storeconfig from magento', error);
      }
      storeConfig.value = {};
    }
  }

  function needsStoreConfig(pathname: string): boolean {
    return isStoreConfigRequiredRoute(
      pathname,
      $globalPageSettings.value?.shopConfig
    );
  }

  if (import.meta.server) {
    const url = useRequestURL();

    if (
      shouldLoadMagentoStoreConfig({
        pathname: url.pathname,
        shopConfig: $globalPageSettings.value?.shopConfig,
        locale: $locale.value,
        isServer: true,
        hasStoreCode: Boolean(storeConfig.value?.store_code)
      })
    ) {
      await refreshMagentoStoreConfig();
    } else {
      storeConfig.value = {};
    }
  }

  if (import.meta.client) {
    watch(
      // Also watch shopConfig so storeConfig loads when GPS hydrates lazily
      // and the user is already on a cart/checkout/account page.
      () =>
        [
          shopStore.value,
          $locale.value,
          $globalPageSettings.value?.shopConfig
        ] as const,
      () => {
        if (needsStoreConfig(useRoute().path)) {
          void refreshMagentoStoreConfig();
        }
      }
    );

    addRouteMiddleware(
      'shop-store-config',
      async (to) => {
        if (
          shouldLoadMagentoStoreConfig({
            pathname: to.path,
            shopConfig: $globalPageSettings.value?.shopConfig,
            locale: $locale.value,
            isServer: false,
            hasStoreCode: Boolean(storeConfig.value?.store_code)
          })
        ) {
          await refreshMagentoStoreConfig();
        }
      },
      { global: true }
    );
  }

  return {
    provide: {
      createProductDetailLink,
      shopStore,
      usesDemoShop,
      storeConfig
    }
  };
});
